import { useMemo, useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Bell,
  ShoppingCart,
  Settings,
  FileText,
  ShoppingBag,
  FolderOpen,
  LogOut,
  Menu,
  CheckCircle,
  XCircle,
  Upload,
  Play,
  Calendar,
  User
} from "lucide-react";
import { useAuth } from "@/utils/authContext";
import { useCart } from "@/utils/cartContext";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation } from "@apollo/client";
import { GET_NOTIFICATIONS, MARK_NOTIFICATIONS_AS_READ } from "@/Graphql/packageQurie";
import { USER_ACCOUNT_QUERY } from "@/Graphql/AuthQueries";
import { generateMockData } from "@/Graphql/MockData";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import logger from '@/utils/logger';
// Real-time notification integration with API

// Helper function to get notification icon and color based on event type
const getNotificationStyle = (eventType: string) => {
  switch (eventType) {
    case 'ECOMMERCE_CAMPAIGN_APPROVED':
      return { icon: CheckCircle, color: 'text-green-600', title: 'Campaign Approved' };
    case 'ECOMMERCE_CREATIVE_ASSIGNED':
      return { icon: Upload, color: 'text-blue-600', title: 'Creative Assigned' };
    case 'ECOMMERCE_CAMPAIGN_REJECTED':
      return { icon: XCircle, color: 'text-red-600', title: 'Campaign Rejected' };
    case 'ECOMMERCE_PROOF_OF_PLAY':
      return { icon: Play, color: 'text-purple-600', title: 'Proof of Play' };
    case 'ECOMMERCE_PAYMENT_SUCCESS':
      return { icon: CheckCircle, color: 'text-green-600', title: 'Payment Successful' };
    case 'ECOMMERCE_CAMPAIGN_ENDING':
      return { icon: Calendar, color: 'text-yellow-600', title: 'Campaign Ending' };
    case 'ECOMMERCE_CREATIVE_REJECTED':
      return { icon: XCircle, color: 'text-red-600', title: 'Creative Rejected' };
    case 'ECOMMERCE_CAMPAIGN_CREATED':
      return { icon: Calendar, color: 'text-blue-600', title: 'Campaign Created' };
    case 'ECOMMERCE_BILLBOARD_ADDED':
      return { icon: Upload, color: 'text-green-600', title: 'Billboard Added' };
    default:
      return { icon: Bell, color: 'text-gray-600', title: 'Notification' };
  }
};

// Helper function to format timestamp
const formatNotificationTime = (timestamp: string) => {
  const now = new Date();
  const notificationTime = new Date(timestamp);
  const diffInMinutes = Math.floor((now.getTime() - notificationTime.getTime()) / (1000 * 60));

  if (diffInMinutes < 1) return 'Just now';
  if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours} hours ago`;

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) return `${diffInDays} days ago`;

  return notificationTime.toLocaleDateString();
};

// Helper function to strip HTML tags from notification message
const stripHtmlTags = (html: string) => {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
};

interface NavigationProps {
  configData?: any[];
  yourLogo?: string;
  base_path?: string;
  cartUrl?: string;
  // Add mode prop to distinguish between header and navigation usage
  mode?: 'header' | 'landing';
}

export default function Navigation({
  configData = [],
  yourLogo,
  base_path = '',
  cartUrl,
  mode = 'landing'
}: NavigationProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn, logout } = useAuth();
  const { cartCount, fetchCartDetails } = useCart();
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Check if we're on the landing page
  const isOnLandingPage = location.pathname === '/' || location.pathname === base_path || location.pathname === `${base_path}/`;

  // Check if mock data is enabled and we should show logged-in navigation
  const isMockMode = import.meta.env.VITE_APP_MOCK_DATA === "true";
  const shouldShowLoggedInNav = isMockMode && !isOnLandingPage;
  const { toast } = useToast();

  // State for mock user data
  const [mockUserData, setMockUserData] = useState<any>(null);
  const [mockNotificationsData, setMockNotificationsData] = useState<any>(null);

  // Fetch mock user data when in mock mode and not on landing page
  useEffect(() => {
    const fetchMockUserData = async () => {
      if (shouldShowLoggedInNav) {
        try {
          const mockData = await generateMockData(USER_ACCOUNT_QUERY);
          setMockUserData(mockData.data.userAccount[0]);
        } catch (error) {
          logger.error('Error fetching mock user data:', error);
        }
      }
    };

    fetchMockUserData();
  }, [shouldShowLoggedInNav]);

  // Fetch mock notifications when in mock mode
  useEffect(() => {
    const fetchMockNotifications = async () => {
      if (isMockMode && shouldShowLoggedInNav) {
        try {
          const mockData = await generateMockData(GET_NOTIFICATIONS);
          if (mockData && mockData.data) {
            setMockNotificationsData(mockData.data);
          }
        } catch (error) {
          logger.error('Error fetching mock notifications:', error);
        }
      }
    };

    fetchMockNotifications();
  }, [isMockMode, shouldShowLoggedInNav]);

  // Local read state management
  const [localReadNotifications, setLocalReadNotifications] = useState<Set<string>>(() => {
    // Load from localStorage on component mount
    const saved = localStorage.getItem('readNotificationIds');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });

  // Save read notifications to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('readNotificationIds', JSON.stringify([...localReadNotifications]));
  }, [localReadNotifications]);

  useEffect(() => {
    fetchCartDetails();
  }, [])
  const [markAsRead] = useMutation(MARK_NOTIFICATIONS_AS_READ);
  // Function to mark notification as read locally
  const markAsReadLocally = (notificationId: string) => {
    const response = markAsRead({
      variables: {
        accessToken: localStorage.getItem('authToken'),
        notificationIds: [notificationId],
        recipientId: localStorage.getItem('userId')
      }
    });
    logger.log("Mark as read response:", response);

    setLocalReadNotifications(prev => new Set([...prev, notificationId]));
  };

  // Function to mark all notifications as read locally
  const markAllAsReadLocally = () => {
    if (!notificationsData?.listNotifications) return;

    const allNotificationIds = notificationsData.listNotifications.map((n: any) => n.id);
    const response = markAsRead({
      variables: {
        accessToken: localStorage.getItem('authToken'),
        notificationIds: allNotificationIds,
        recipientId: localStorage.getItem('userId')
      }
    });
    logger.log("Mark all as read response:", response);
    setLocalReadNotifications(prev => new Set([...prev, ...allNotificationIds]));
  };

  // Fetch real notifications from API - only when needed and NOT in mock mode
  const { data: notificationsData, refetch: refetchNotifications } = useQuery(GET_NOTIFICATIONS, {
    variables: {
      accessToken: localStorage.getItem('authToken'),
      companyId: localStorage.getItem('companyId'),
      userId: localStorage.getItem('userId')
    },
    skip: isMockMode || !(isLoggedIn || shouldShowLoggedInNav) || !localStorage.getItem('authToken'),
    fetchPolicy: 'cache-first', // Use cache first to reduce API calls
    notifyOnNetworkStatusChange: false // Prevent extra renders
  });

  // Fetch notifications when dropdown is opened (with throttling)
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const handleNotificationToggle = (open: boolean) => {
    setIsNotificationOpen(open);
    if (open && (isLoggedIn || shouldShowLoggedInNav) && !isMockMode) {
      // Only refetch if it's been more than 10 seconds since last fetch and not in mock mode
      const now = Date.now();
      if (now - lastFetchTime > 10000) {
        refetchNotifications();
        setLastFetchTime(now);
      }
    }
  };



  // Transform API notifications to UI format with local read state
  const notifications = useMemo(() => {
    // Use mock data if in mock mode, otherwise use real API data
    const sourceData = isMockMode ? mockNotificationsData : notificationsData;

    if (!sourceData?.listNotifications) return [];

    return sourceData.listNotifications.map((notification: any) => {
      const style = getNotificationStyle(notification.event);

      // Check if notification is read: either from API or locally
      const isReadOnServer = !notification.newMessage || notification.readAt;
      const isReadLocally = localReadNotifications.has(notification.id);
      const isRead = isReadOnServer || isReadLocally;

      return {
        id: notification.id,
        type: notification.event,
        title: style.title,
        message: stripHtmlTags(notification.notificationMessage),
        time: formatNotificationTime(notification.eventAt),
        unread: !isRead, // Combine server and local read state
        icon: style.icon,
        color: style.color
      };
    });
  }, [notificationsData, mockNotificationsData, localReadNotifications, isMockMode]);

  // Clean up localStorage: remove read IDs that are no longer in the API response
  useEffect(() => {
    const sourceData = isMockMode ? mockNotificationsData : notificationsData;

    if (sourceData?.listNotifications) {
      const currentNotificationIds = new Set(
        sourceData.listNotifications.map((n: any) => n.id)
      );

      // Get current locally read notifications
      const currentLocalRead = Array.from(localReadNotifications);

      // Find stale IDs (locally read but not in current API response)
      const staleIds = currentLocalRead.filter(id => !currentNotificationIds.has(id));

      // Remove stale IDs from localStorage if they're older than 7 days
      if (staleIds.length > 0) {
        const updatedReadNotifications = new Set(currentLocalRead.filter(id => currentNotificationIds.has(id)));
        setLocalReadNotifications(updatedReadNotifications);
        localStorage.setItem('notification_read_ids', JSON.stringify(Array.from(updatedReadNotifications)));
        logger.log(`🧹 Cleaned up ${staleIds.length} stale notification IDs from localStorage`);
      }
    }
  }, [notificationsData, mockNotificationsData, localReadNotifications, isMockMode]);

  // Force re-render when cart updates
  useEffect(() => {
    const handleCartUpdate = () => {
      // This will trigger a re-render when cartCount changes
    };
    window.addEventListener('cartUpdated', handleCartUpdate);
    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
    };
  }, [cartCount]);

  // Config loading state and checks
  const configLoaded = Array.isArray(configData) && configData.length > 0;

  // Cache config lookups for better performance
  const { isExploreEnabled, isFaqEnabled, isLoginEnabled, isLandingPage } = useMemo(() => {
    logger.log("ConfigData-->", configData);

    if (!configLoaded) return {
      isExploreEnabled: mode === 'landing',
      isFaqEnabled: mode === 'landing',
      isLoginEnabled: mode === 'landing',
      isLandingPage: mode === 'landing'
    };

    const userRegPage = configData.find((page: any) => page.name.toLowerCase().includes("landing page"));
    if (!userRegPage) return {
      isExploreEnabled: mode === 'landing',
      isFaqEnabled: mode === 'landing',
      isLoginEnabled: mode === 'landing',
      isLandingPage: mode === 'landing'
    };

    const loginConfig = userRegPage.configurations?.find((c: any) => c.key === "login");
    const exploreField = userRegPage?.configurations?.find((f: any) => f.key === "Explore");
    const faqField = userRegPage?.configurations?.find((f: any) => f.key === "faq");
    const landingPageField = userRegPage?.configurations?.find((f: any) => f.key === "do_you_need_landing_page");

    return {
      isExploreEnabled: exploreField ? !!exploreField.default : (mode === 'landing'),
      isLoginEnabled: loginConfig ? !!loginConfig.default : (mode === 'landing'),
      isFaqEnabled: faqField ? !!faqField.default : (mode === 'landing'),
      // CHANGED: Explicitly check for "yes" or true, as "no" is truthy in JS
      isLandingPage: landingPageField
        ? (landingPageField.default === "yes" || landingPageField.default === true)
        : (mode === 'landing'),
    };
  }, [configLoaded, configData, mode]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const handleLogin = () => {
    navigate(`${base_path}/login`);
    setIsMenuOpen(false);
  };

  const handleRegister = () => {
    navigate(`${base_path}/sign-up`);
    setIsMenuOpen(false);
  };

  const userAccountData = localStorage.getItem('userAccountData');
  const parsedData = userAccountData ? JSON.parse(userAccountData) : [];
  const user = useMemo(() => parsedData[0] || {}, [userAccountData]);

  const getInitials = () => {
    // Use mock data if in mock mode and not on landing page
    if (shouldShowLoggedInNav && mockUserData) {
      const firstName = mockUserData.firstName || '';
      const lastName = mockUserData.lastName || '';
      return (firstName[0] || 'M') + (lastName[0] || 'U');
    }

    // Original logic for real user data
    const firstName = user?.firstName || '';
    const lastName = user?.lastName || '';
    return (firstName[0] || 'U') + (lastName[0] || '');
  };

  const getUserDetails = () => {
    // Use mock data if in mock mode and not on landing page
    if (shouldShowLoggedInNav && mockUserData) {
      return {
        displayName: `${mockUserData.firstName} ${mockUserData.lastName}`.trim() || mockUserData.login || 'Mock User',
        email: mockUserData.email || 'mock@example.com',
        firstName: mockUserData.firstName || 'Mock',
        lastName: mockUserData.lastName || 'User'
      };
    }

    // Original logic for real user data
    const userName = localStorage.getItem('userName') || '';
    const userEmail = localStorage.getItem('userEmail') || '';
    const userNameLogin = localStorage.getItem('userNameLogin') || '';

    const userAccountDataStr = localStorage.getItem('userAccountData');
    let firstName = '';
    let lastName = '';

    if (userAccountDataStr) {
      try {
        const userAccountData = JSON.parse(userAccountDataStr);
        if (userAccountData && userAccountData[0]) {
          firstName = userAccountData[0].firstName || '';
          lastName = userAccountData[0].lastName || '';
        }
      } catch (e) {
        logger.warn('Error parsing user account data:', e);
      }
    }

    return {
      displayName: userName || `${firstName} ${lastName}`.trim() || userNameLogin || 'User',
      email: userEmail || userNameLogin || '',
      firstName,
      lastName
    };
  };

  const userDetails = getUserDetails();
  const unreadCount = (isLoggedIn || shouldShowLoggedInNav) ? notifications.filter((n: any) => n.unread).length : 0;

  // Landing page specific navigation links
  const NavLinks = ({ mobile = false }) => (
    <>
      {mode === 'landing' && isExploreEnabled && (
        <Button
          variant="ghost"
          onClick={() => {
            if (isLoggedIn || shouldShowLoggedInNav) {
              // Check if already on start-campaign page
              if (location.pathname.includes('/propertylist')) {
                toast({
                  title: "Already on Start Campaign",
                  description: "You are already on the Start Campaign page.",
                  variant: "default"
                });
                setIsMenuOpen(false);
                return;
              }
              localStorage.setItem('clearStartCampaignFilters', 'true');
              navigate(`${base_path}/propertylist`);
            } else {
              navigate(`${base_path}/explore`);
            }
            setIsMenuOpen(false);
          }}
          className={`${mobile
            ? 'w-full justify-start py-3 px-4 text-left rounded-md hover:bg-emerald-50 transition-colors duration-200'
            : 'text-gray-700 hover:text-emerald-600 hover:bg-emerald-50 transition-colors duration-200 text-sm'
            }`}
          data-testid="nav-inventory"
        >
          Explore
        </Button>
      )}
      {mode === 'landing' && (
        <>
          <Button
            variant="ghost"
            onClick={() => scrollToSection('pricing')}
            className={`${mobile
              ? 'w-full justify-start py-3 px-4 text-left rounded-md hover:bg-emerald-50 transition-colors duration-200'
              : 'text-gray-700 hover:text-emerald-600 hover:bg-emerald-50 transition-colors duration-200 text-sm'
              }`}
            data-testid="nav-pricing"
          >
            Pricing
          </Button>
          <Button
            variant="ghost"
            onClick={() => scrollToSection('coverage')}
            className={`${mobile
              ? 'w-full justify-start py-3 px-4 text-left rounded-md hover:bg-emerald-50 transition-colors duration-200'
              : 'text-gray-700 hover:text-emerald-600 hover:bg-emerald-50 transition-colors duration-200 text-sm'
              }`}
            data-testid="nav-coverage"
          >
            Coverage
          </Button>
          {mode === 'landing' && isFaqEnabled && (
            <Button
              variant="ghost"
              onClick={() => {
                navigate(`${base_path}/faq`);
                setIsMenuOpen(false);
              }}
              className={`${mobile
                ? 'w-full justify-start py-3 px-4 text-left rounded-md hover:bg-emerald-50 transition-colors duration-200'
                : 'text-gray-700 hover:text-emerald-600 hover:bg-emerald-50 transition-colors duration-200 text-sm'
                }`}
              data-testid="nav-faq"
            >
              FAQ
            </Button>
          )}
          <Button
            variant="ghost"
            onClick={() => scrollToSection('contact')}
            className={`${mobile
              ? 'w-full justify-start py-3 px-4 text-left rounded-md hover:bg-emerald-50 transition-colors duration-200'
              : 'text-gray-700 hover:text-emerald-600 hover:bg-emerald-50 transition-colors duration-200 text-sm'
              }`}
            data-testid="nav-contact"
          >
            Contact
          </Button>
        </>
      )}
      {/* Header mode navigation links */}
      {mode === 'header' && (
        <>
          <Button
            variant="ghost"
            className={`${window.location.pathname === "/" ? "text-emerald-600 bg-emerald-50" : "text-gray-900 hover:text-emerald-600 hover:bg-emerald-100"} transition-colors duration-200`}
            onClick={() => navigate(!isLandingPage ? `${base_path}/my-campaigns` : "/")}
          >
            Home
          </Button>
          {/* Explore button: show only if config loaded, enabled, and not logged in */}
          {configLoaded && isExploreEnabled && !(isLoggedIn || shouldShowLoggedInNav) && (
            <Button
              variant="ghost"
              className={`${window.location.pathname === `${base_path}/explore` ? "text-emerald-600 bg-emerald-50" : "text-gray-600 hover:text-emerald-600 hover:bg-emerald-100"} transition-colors duration-200`}
              onClick={() => navigate(`${base_path}/explore`)}
            >
              Explore
            </Button>
          )}
        </>
      )}
    </>
  );

  return (
    <nav className={`bg-white shadow-sm ${mode === 'landing' ? 'sticky top-0 z-50' : ''} border-b`}>
      <div className="max-w-[95vw] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo Section */}
          <div className="flex items-center space-x-8">
            <Link
              to={(!isLandingPage && (isLoggedIn || shouldShowLoggedInNav))
                ? `${base_path}/my-campaigns`
                : `${base_path}/`
              }
              className="flex items-center"
            >
              {yourLogo && (
                <img
                  src={yourLogo}
                  alt="Logo"
                  className="h-8 w-auto object-contain sm:h-10"
                  style={{ maxWidth: mode === 'landing' ? '10rem' : '160px' }}
                  data-testid="logo-image"
                />
              )}
            </Link>

            {/* Desktop Navigation Links */}
            {/* <nav className="hidden md:flex space-x-8">
              <NavLinks />
            </nav> */}
          </div>

          {/* Actions Section */}
          <div className="flex items-center space-x-4">
            {/* Desktop Navigation Links for landing mode */}
            {mode === 'landing' && (
              <div className="hidden md:flex items-center space-x-4">
                <NavLinks />
              </div>
            )}

            {/* Cart */}
            <Link to={cartUrl ?? `${base_path}/mycart`} data-testid="button-cart">
              <Button
                variant="ghost"
                size="sm"
                className="relative hover:bg-emerald-100 transition-all duration-300 flex-shrink-0"
              >
                <ShoppingCart className="h-5 w-5 text-emerald-600 sm:h-6 sm:w-6" />
                {cartCount > 0 && (
                  <Badge className="absolute -top-2 -right-2 h-5 w-5 flex items-center justify-center p-0 text-xs bg-emerald-600">
                    {cartCount}
                  </Badge>
                )}
              </Button>
            </Link>

            {/* User Actions - Conditional based on login status or mock mode */}
            {isLoggedIn || shouldShowLoggedInNav ? (
              <>
                {/* Notifications Dropdown */}
                <DropdownMenu open={isNotificationOpen} onOpenChange={handleNotificationToggle}>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="relative hover:bg-emerald-100 transition-colors duration-200 flex-shrink-0">
                      <Bell className="h-5 w-5 text-emerald-600 sm:h-6 sm:w-6" />
                      {unreadCount > 0 && (
                        <span className={`absolute -top-2 -right-2 bg-red-600 text-white rounded-full h-4 w-4 flex items-center justify-center p-0 sm:h-5 sm:w-5 font-bold ${unreadCount > 99 ? 'text-[0.5rem] sm:text-[0.6rem]' : 'text-xs'}`}>
                          {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-80 bg-white border border-gray-200 shadow-lg rounded-md">
                    <div className="p-3 border-b bg-white">
                      <h3 className="font-semibold text-gray-900">Notifications</h3>
                      <p className="text-sm text-gray-500">{unreadCount} new notifications</p>
                    </div>
                    <div className="max-h-80 overflow-y-auto notification-scrollbar">
                      {(isLoggedIn || shouldShowLoggedInNav) && notifications.length > 0 ? (
                        notifications.map((notification: any) => {
                          const IconComponent = notification.icon;
                          return (
                            <DropdownMenuItem
                              key={notification.id}
                              className={`p-4 border-b last:border-b-0 cursor-pointer hover:bg-gray-100 ${notification.unread ? 'bg-blue-50' : ''}`}
                              onClick={() => {
                                if (notification.unread) {
                                  markAsReadLocally(notification.id);
                                }
                              }}
                            >
                              <div className="flex items-start space-x-3 w-full">
                                <div className={`flex-shrink-0 ${notification.color}`}>
                                  <IconComponent className="h-5 w-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between">
                                    <p className={`text-sm font-medium text-gray-900 ${notification.unread ? 'font-semibold' : ''} break-words pr-2 flex-1`}>
                                      {notification.title}
                                    </p>
                                    {notification.unread && (
                                      <div className="h-2 w-2 bg-blue-600 rounded-full flex-shrink-0 mt-1"></div>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-600 mt-1 break-words">{notification.message}</p>
                                  <p className="text-xs text-gray-400 mt-2">{notification.time}</p>
                                </div>
                              </div>
                            </DropdownMenuItem>
                          );
                        })
                      ) : (
                        <div className="p-4 text-center text-gray-500">
                          {(isLoggedIn || shouldShowLoggedInNav) ? 'No notifications yet' : 'Login to view notifications'}
                        </div>
                      )}
                    </div>
                    <div className="p-3 border-t bg-white flex gap-2">
                      <Button
                        variant="ghost"
                        className="flex-1 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-100"
                        onClick={markAllAsReadLocally}
                        disabled={unreadCount === 0}
                      >
                        Mark All Read
                      </Button>
                      {/* <Button variant="ghost" className="flex-1 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-100">
                        View All
                      </Button> */}
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* User Profile Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="relative hover:bg-emerald-100 transition-colors duration-200 flex-shrink-0">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-lg font-bold sm:w-9 sm:h-9">
                        {getInitials()}
                      </div>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-white border border-gray-200 shadow-lg rounded-md">
                    <DropdownMenuItem disabled>
                      <div className="flex flex-col">
                        <span className="font-medium">{userDetails.displayName}</span>
                        <span className="text-xs text-gray-500">{userDetails.email}</span>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem>
                      <div
                        onClick={() => {
                          // Check if already on account page
                          if (location.pathname.includes('/account')) {
                            toast({
                              title: "Already on My Account",
                              description: "You are already on the My Account page.",
                              variant: "default"
                            });
                            return;
                          }
                          window.location.replace(`${window.location.origin}${base_path}/account`);
                        }}
                        className="flex items-center hover:bg-gray-100 transition-colors duration-200 cursor-pointer"
                      >
                        {/* <Settings className="mr-2 h-4 w-4 text-emerald-600" /> */}
                        <User className="mr-2 h-4 w-4 text-emerald-600" />

                        My Account
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <div
                        onClick={() => {
                          // Check if already on campaigns page
                          if (location.pathname.includes('/my-campaigns')) {
                            toast({
                              title: "Already on My Campaigns",
                              description: "You are already on the My Campaigns page.",
                              variant: "default"
                            });
                            return;
                          }
                          window.location.replace(`${window.location.origin}${base_path}/my-campaigns`);
                        }}
                        className="flex items-center hover:bg-gray-100 transition-colors duration-200 cursor-pointer"
                      >
                        <FileText className="mr-2 h-4 w-4 text-emerald-600" />
                        My Campaigns
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <div
                        onClick={() => {
                          // Check if already on cart page
                          if (location.pathname.includes('/mycart')) {
                            toast({
                              title: "Already on My Cart",
                              description: "You are already on the My Cart page.",
                              variant: "default"
                            });
                            return;
                          }
                          window.location.replace(cartUrl ?? `${base_path}/mycart`);
                        }}
                        className="flex items-center hover:bg-gray-100 transition-colors duration-200 cursor-pointer"
                      >
                        <ShoppingBag className="mr-2 h-4 w-4 text-emerald-600" />
                        My Cart {cartCount > 0 && <span className="ml-1 text-xs text-gray-500">({cartCount})</span>}
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <div
                        onClick={() => {
                          // Check if already on content hub page
                          if (location.pathname.includes('/contenthub')) {
                            toast({
                              title: "Already on Content Hub",
                              description: "You are already on the Content Hub page.",
                              variant: "default"
                            });
                            return;
                          }
                          window.location.replace(`${window.location.origin}${base_path}/contenthub`);
                        }}
                        className="flex items-center hover:bg-gray-100 transition-colors duration-200 cursor-pointer"
                      >
                        <FolderOpen className="mr-2 h-4 w-4 text-emerald-600" />
                        Content Hub
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <div
                        onClick={() => {
                          // Check if already on start-campaign page
                          if (location.pathname.includes('/propertylist')) {
                            toast({
                              title: "Already on Start Campaign",
                              description: "You are already on the Start Campaign page.",
                              variant: "default"
                            });
                            return;
                          }
                          localStorage.setItem('clearStartCampaignFilters', 'true');
                          window.location.replace(`${window.location.origin}${base_path}/propertylist`);
                        }}
                        className="flex items-center hover:bg-gray-100 transition-colors duration-200 cursor-pointer"
                      >
                        <FileText className="mr-2 h-4 w-4 text-emerald-600" />
                        Start Campaign
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => logout()} className="text-red-600 hover:bg-red-100 hover:text-red-700 transition-colors duration-200 cursor-pointer">
                      <LogOut className="mr-2 h-4 w-4" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              /* Login/Register buttons for non-logged in users */
              <div className="flex gap-2">
                {configLoaded && isLoginEnabled && !(isLoggedIn || shouldShowLoggedInNav) && (
                  <>
                    <div className="hidden md:flex items-center space-x-2">
                      <Button
                        onClick={handleRegister}
                        variant="outline"
                        size="sm"
                        className="border-emerald-600 text-emerald-600 hover:bg-emerald-100 hover:border-emerald-700 transition-colors duration-200"
                        data-testid="button-register"
                      >
                        Register
                      </Button>
                      <Button
                        onClick={handleLogin}
                        variant="default"
                        size="sm"
                        className={mode === 'landing'
                          ? "bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:from-emerald-600 hover:to-emerald-700 transition-all duration-300 shadow-lg hover:shadow-emerald-500/40"
                          : "btn-emerald-gradient text-white hover:opacity-90 transition-opacity duration-200"
                        }
                        data-testid="button-login"
                      >
                        Login
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Mobile Menu Trigger */}
            <div className="md:hidden flex items-center">
              {mode === 'landing' ? (
                <Sheet>
                  <SheetTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid="button-mobile-menu"
                      className="hover:bg-emerald-100 transition-colors duration-200"
                      onClick={() => setIsMenuOpen(!isMenuOpen)}
                    >
                      <Menu className="h-6 w-6 sm:h-7 sm:w-7" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="right" className="w-80 sm:w-96">
                    <SheetHeader>
                      <SheetTitle className="flex items-center justify-start">
                        <img
                          src={yourLogo}
                          alt="MW Ads Logo"
                          className="h-8 w-auto object-contain sm:h-9"
                          style={{ maxWidth: '10rem' }}
                        />
                      </SheetTitle>
                    </SheetHeader>
                    <div className="mt-6 flex flex-col space-y-2">
                      <NavLinks mobile />
                      {!(isLoggedIn || shouldShowLoggedInNav) && isLoginEnabled && (
                        <div className="pt-4 space-y-3 border-t">
                          <Button
                            onClick={handleRegister}
                            variant="outline"
                            className="w-full border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white hover:border-emerald-600 transition-all duration-300"
                            data-testid="button-mobile-register"
                          >
                            Register
                          </Button>
                          <Button
                            onClick={handleLogin}
                            className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:from-emerald-600 hover:to-emerald-700 transition-all duration-300 shadow-lg hover:shadow-emerald-500/40"
                            data-testid="button-mobile-login"
                          >
                            Login
                          </Button>
                        </div>
                      )}
                      {(isLoggedIn || shouldShowLoggedInNav) && (
                        <div className="pt-4 space-y-3 border-t">
                          <Button
                            variant="ghost"
                            className="w-full justify-start hover:bg-emerald-100 transition-colors duration-200"
                            onClick={() => {
                              // Check if already on cart page
                              if (location.pathname.includes('/mycart')) {
                                toast({
                                  title: "Already on My Cart",
                                  description: "You are already on the My Cart page.",
                                  variant: "default"
                                });
                                return;
                              }
                              navigate(cartUrl ?? `${base_path}/mycart`)
                            }}
                          >
                            <ShoppingBag className="mr-2 h-4 w-4 text-emerald-600" />
                            My Cart {cartCount > 0 && <span className="ml-1 text-xs">({cartCount})</span>}
                          </Button>
                          <Button
                            variant="ghost"
                            className="w-full justify-start hover:bg-emerald-100 transition-colors duration-200"
                            onClick={() => {
                              // Check if already on content hub page
                              if (location.pathname.includes('/contenthub')) {
                                toast({
                                  title: "Already on Content Hub",
                                  description: "You are already on the Content Hub page.",
                                  variant: "default"
                                });
                                return;
                              }
                              navigate(`${base_path}/contenthub`)
                            }}
                          >
                            <FolderOpen className="mr-2 h-4 w-4 text-emerald-600" />
                            Content Hub
                          </Button>
                          <Button
                            variant="ghost"
                            className="w-full justify-start hover:bg-emerald-100 transition-colors duration-200"
                            onClick={() => {
                              // Check if already on start-campaign page
                              if (location.pathname.includes('/propertylist')) {
                                toast({
                                  title: "Already on Start Campaign",
                                  description: "You are already on the Start Campaign page.",
                                  variant: "default"
                                });
                                return;
                              }
                              localStorage.setItem('clearStartCampaignFilters', 'true');
                              navigate(`${base_path}/propertylist`)
                            }}
                          >
                            <FileText className="mr-2 h-4 w-4 text-emerald-600" />
                            Start Campaign
                          </Button>
                          <Button
                            onClick={() => logout()}
                            variant="outline"
                            className="w-full border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white hover:border-red-600 transition-all duration-300"
                            data-testid="button-mobile-logout"
                          >
                            <LogOut className="mr-2 h-4 w-4" />
                            Logout
                          </Button>
                        </div>
                      )}
                    </div>
                  </SheetContent>
                </Sheet>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  className="hover:bg-emerald-100 transition-colors duration-200"
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                >
                  <Menu className="h-5 w-5" />
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Mobile menu for header mode */}
        {mode === 'header' && isMenuOpen && (
          <div className="md:hidden py-4 border-t">
            <div className="flex flex-col space-y-2">
              <Button variant="ghost" className="w-full justify-start hover:bg-emerald-100 transition-colors duration-200" onClick={() => { setIsMenuOpen(false); navigate(!isLandingPage ? `${base_path}/my-campaigns` : `${base_path}/`); }}>
                Home
              </Button>
              <Button variant="ghost" className="w-full justify-start hover:bg-emerald-100 transition-colors duration-200" onClick={() => { setIsMenuOpen(false); navigate("/locations"); }}>
                Locations
              </Button>
              <Button variant="ghost" className="w-full justify-start hover:bg-emerald-100 transition-colors duration-200" onClick={() => { setIsMenuOpen(false); navigate(`${base_path}/my-campaigns`); }}>
                Campaigns
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}